import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Snackbar,
  Alert, Typography, FormControl, InputLabel, Select, MenuItem, Box
} from '@mui/material';
import api from '../api';
import { useParams, useNavigate } from "react-router-dom";

export default function CompetencesTable(props) {
  const params = useParams();
  const navigate = useNavigate();
  const activiteId = props.activiteId || params.activiteId;
  const [competences, setCompetences] = useState([]);
  const [activites, setActivites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ nom: '', description: '', activite: activiteId || '', valeur: 1 });
  const [editId, setEditId] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Charger la liste d'activités si besoin
  useEffect(() => {
    if (!activiteId) {
      api.get('/activites').then(res =>
        setActivites(Array.isArray(res.data) ? res.data : [])
      );
    }
  }, [activiteId]);

  // Charger les compétences liées à l'activité sélectionnée
  const fetchCompetences = async (aid = activiteId || form.activite) => {
    setLoading(true);
    setError('');
    if (!aid) {
      setCompetences([]);
      setLoading(false);
      return;
    }
    try {
      const res = await api.get(`/competences/${aid}`);
      setCompetences(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.response?.data?.error || "Erreur lors du chargement des compétences.");
      setCompetences([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (activiteId) setForm(f => ({ ...f, activite: activiteId }));
    fetchCompetences(activiteId);
    // eslint-disable-next-line
  }, [activiteId]);

  const handleOpen = (c = null) => {
    setOpen(true);
    setForm({
      nom: c?.nom || '',
      description: c?.description || '',
      activite: c?.activite?._id || activiteId || '',
      valeur: c?.valeur ?? 1
    });
    setEditId(c?._id || null);
  };

  const handleClose = () => {
    setOpen(false);
    setForm({ nom: '', description: '', activite: activiteId || '', valeur: 1 });
    setEditId(null);
  };

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.nom || !form.activite) {
      setSnack({ open: true, message: "Nom et activité obligatoires", severity: 'error' });
      return;
    }
    try {
      if (editId) {
        await api.put(`/competences/${editId}`, {
          nom: form.nom,
          description: form.description,
          valeur: Number(form.valeur)
        });
        setSnack({ open: true, message: "Compétence modifiée", severity: 'success' });
      } else {
        await api.post('/competences', {
          nom: form.nom,
          description: form.description,
          activite: form.activite,
          valeur: Number(form.valeur)
        });
        setSnack({ open: true, message: "Compétence ajoutée", severity: 'success' });
      }
      handleClose();
      fetchCompetences(form.activite);
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || "Erreur", severity: 'error' });
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Supprimer cette compétence ?')) return;
    try {
      await api.delete(`/competences/${id}`);
      setSnack({ open: true, message: "Compétence supprimée", severity: 'success' });
      fetchCompetences(form.activite);
    } catch (err) {
      setSnack({ open: true, message: "Erreur lors de la suppression", severity: 'error' });
    }
  };

  if (loading) return <Typography sx={{ mt: 6, textAlign: 'center' }}>Chargement des compétences...</Typography>;
  if (error) return (
    <Alert severity="error" sx={{ m: 4 }}>
      {error}
    </Alert>
  );
  if (!competences || !Array.isArray(competences)) return (
    <Alert severity="error" sx={{ m: 4 }}>
      Problème de données reçues du serveur.
    </Alert>
  );

  return (
    <TableContainer component={Paper} sx={{ mt: 4 }}>
      {/* Bouton retour vers la liste des activités */}
      <Box sx={{ p: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/activites')}>← Retour aux activités</Button>
      </Box>

      <Typography variant="h6" sx={{ m: 2 }}>Compétences de l'activité</Typography>
      <Button variant="contained" sx={{ m: 2 }} onClick={() => handleOpen()}>Ajouter une compétence</Button>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Nom</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Valeur</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {competences.map(c => (
            <TableRow key={c._id}>
              <TableCell>{c.nom}</TableCell>
              <TableCell>{c.description}</TableCell>
              <TableCell>{c.valeur ?? 1}</TableCell>
              <TableCell>
                <Button size="small" onClick={() => handleOpen(c)}>Modifier</Button>
                <Button size="small" color="error" onClick={() => handleDelete(c._id)}>Supprimer</Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editId ? "Modifier la compétence" : "Ajouter une compétence"}</DialogTitle>
        <DialogContent>
          <form id="competenceForm" onSubmit={handleSubmit}>
            <TextField label="Nom" name="nom" value={form.nom} onChange={handleChange} margin="normal" fullWidth required />
            <TextField label="Description" name="description" value={form.description} onChange={handleChange} margin="normal" fullWidth />
            <TextField label="Valeur pour la note" name="valeur" type="number" value={form.valeur} onChange={handleChange} margin="normal" fullWidth inputProps={{ min: 0, step: 0.5 }} />
            {!activiteId && (
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="activite-label">Activité</InputLabel>
                <Select
                  labelId="activite-label"
                  label="Activité"
                  name="activite"
                  value={form.activite}
                  onChange={handleChange}
                  required
                >
                  {activites.map(a => (
                    <MenuItem key={a._id} value={a._id}>{a.nom}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button type="submit" form="competenceForm" variant="contained">Sauvegarder</Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </TableContainer>
  );
}